import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Chip,
} from "@mui/material";
import {
  DragIndicator as DragIndicatorIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Lock as LockIcon,
} from "@mui/icons-material";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { NotificationManager } from "react-notifications";
import {
  ReorderDropdownListItems,
  getDropdownListErrorMessage,
} from "../../../services/DropdownListService";

/**
 * Re-order the items of one dropdown list (same pattern as ReorderQuoteTransactionsDialog).
 * Items can be dragged or nudged with the arrow buttons; saving writes the new order.
 * System items ('Other') are not movable and always stay last.
 */
export default function ReorderDropdownItemsDialog({ open, onClose, listType, title, items, onReorderComplete }) {
  const [ordered, setOrdered] = useState([]);
  const [saving, setSaving] = useState(false);

  const systemItems = (items || []).filter((item) => item.isSystem);

  // Always start from the stored order, not the (possibly searched) table view.
  useEffect(() => {
    if (!open) return;
    setOrdered(
      (items || [])
        .filter((item) => !item.isSystem)
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || (a.id ?? 0) - (b.id ?? 0))
    );
  }, [open, items]);

  const move = (index, offset) => {
    const targetIndex = index + offset;
    if (targetIndex < 0 || targetIndex >= ordered.length) return;

    const reordered = [...ordered];
    [reordered[index], reordered[targetIndex]] = [reordered[targetIndex], reordered[index]];
    setOrdered(reordered);
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    if (result.destination.index === result.source.index) return;

    const reordered = [...ordered];
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    setOrdered(reordered);
  };

  const handleSave = async () => {
    if (ordered.length === 0) {
      onClose();
      return;
    }

    setSaving(true);
    try {
      await ReorderDropdownListItems(listType, ordered.map((item) => item.id));
      NotificationManager.success("Item order saved", "Success");
      if (onReorderComplete) onReorderComplete();
      onClose();
    } catch (error) {
      NotificationManager.error(await getDropdownListErrorMessage(error, "Error saving the item order."), "Error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>Re-order {title}</DialogTitle>

      <DialogContent dividers>
        {ordered.length === 0 ? (
          <Typography variant="body2" sx={{ py: 4 }} align="center">
            There are no items in this list to re-order.
          </Typography>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Drag an item, or use the arrows, to set the order items appear in the dropdown.
              Click Save Changes to apply.
            </Typography>

            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId={`dropdown-${listType}`} direction="vertical">
                {(provided) => (
                  <Box
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    {ordered.map((item, index) => (
                      <Draggable key={item.id} draggableId={item.id.toString()} index={index}>
                        {(provided, snapshot) => (
                          <Box
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              px: 1,
                              py: 0.5,
                              border: "1px solid",
                              borderColor: "divider",
                              borderRadius: 1,
                              backgroundColor: snapshot.isDragging ? "action.selected" : "background.paper",
                            }}
                            style={provided.draggableProps.style}
                          >
                            <DragIndicatorIcon fontSize="small" color="action" />

                            <Typography variant="body2" sx={{ width: 28, color: "text.secondary" }}>
                              {index + 1}.
                            </Typography>

                            <Typography variant="body2" noWrap title={item.value} sx={{ flexGrow: 1, minWidth: 0 }}>
                              {item.value}
                            </Typography>

                            {!item.isActive && <Chip size="small" label="Inactive" variant="outlined" />}

                            <IconButton
                              size="small"
                              aria-label="move up"
                              disabled={index === 0}
                              onClick={() => move(index, -1)}
                            >
                              <ArrowUpwardIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              aria-label="move down"
                              disabled={index === ordered.length - 1}
                              onClick={() => move(index, 1)}
                            >
                              <ArrowDownwardIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </Box>
                )}
              </Droppable>
            </DragDropContext>

            {/* System items are shown for context only; they always stay at the bottom. */}
            {systemItems.map((item) => (
              <Box
                key={item.id}
                title="System item: always listed last"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1,
                  py: 0.75,
                  mt: 1,
                  border: "1px dashed",
                  borderColor: "divider",
                  borderRadius: 1,
                  color: "text.disabled",
                }}
              >
                <LockIcon fontSize="small" />
                <Typography variant="body2" sx={{ flexGrow: 1 }}>
                  {item.value} (always last)
                </Typography>
              </Box>
            ))}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={saving || ordered.length === 0}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
}
